import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { api, selectedCompanyId } from "../lib/api";
import { cn } from "../lib/utils";
import { formatBytes, formatTimestamp } from "../lib/format";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";

export const Route = createFileRoute("/ingest")({
  component: desktopOnlyRoute(IngestPage),
});

interface UploadResult {
  ok: boolean;
  duplicate?: boolean;
  fileId?: string;
  inserted?: number;
  updated?: number;
  duplicates?: number;
  parsed?: number;
  rows?: number;
  skipped?: number;
  warnings?: string[];
}

interface IngestFile {
  id: string;
  kind: string;
  filename: string;
  sha256: string;
  sizeBytes: number;
  uploadedAt: string;
  rowCount: number | null;
  parseWarnings: string[] | null;
  uploaderEmail: string | null;
  uploaderDisplayName: string | null;
}

function IngestPage() {
  return (
    <div className="p-4 space-y-6 max-w-5xl">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <DropSection
          title="DAM Schedule"
          description="BRM day-ahead schedule xlsx. Creates or updates the DAM schedule for the file's delivery date."
          endpoint="/api/ingest/dam-schedule"
          kind="dam-schedule"
          accept=".xlsx,.xls"
        />
        <DropSection
          title="Trades Blotter"
          description="BRMID + BRMDA combined xlsx OR BRM Intraday 'My Trades' CSV. Deduped on (market, trade_id)."
          endpoint="/api/ingest/trades"
          kind="trades"
          accept=".xlsx,.xls,.csv"
        />
        <DropSection
          title="Final Imbalance Prices"
          description="DAMAS-style export or template with deliveryDate, ISP/qhIndex, positivePrice, negativePrice."
          endpoint="/api/ingest/imbalance-final-prices"
          kind="imbalance-final-prices"
          accept=".xlsx,.xls,.csv"
        />
      </div>
      <HistoryTable />
    </div>
  );
}

function DropSection({
  title,
  description,
  endpoint,
  kind,
  accept,
}: {
  title: string;
  description: string;
  endpoint: string;
  kind: string;
  accept: string;
}) {
  const acceptRegex = new RegExp(
    "\\.(" + accept.split(",").map((s) => s.trim().replace(/^\./, "")).join("|") + ")$",
    "i"
  );
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: selectedCompanyId() ? { "x-company-id": selectedCompanyId()! } : undefined,
        body: fd,
      });
      const body = (await res.json()) as UploadResult & {
        error?: string;
        message?: string;
      };
      if (!res.ok) throw new Error(body.message ?? body.error ?? res.statusText);
      return body;
    },
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["ingest-files"] });
      void queryClient.invalidateQueries({ queryKey: ["portfolios"] });
      void queryClient.invalidateQueries({ queryKey: ["delivery-days"] });
      void queryClient.invalidateQueries({ queryKey: ["balancing"] });
      void queryClient.invalidateQueries({ queryKey: ["grid"] });
    },
    onError: (e) => {
      setError((e as Error).message);
      setResult(null);
    },
  });

  const submit = useCallback(
    (file: File) => {
      if (!acceptRegex.test(file.name)) {
        setError(`Unsupported file type: ${file.name}`);
        return;
      }
      mutation.mutate(file);
    },
    [mutation, acceptRegex]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) submit(file);
    },
    [submit]
  );

  return (
    <div className="space-y-3">
      <header>
        <h2 className="font-semibold">{title}</h2>
        <p className="text-muted text-xs">{description}</p>
      </header>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          "cursor-pointer border-2 border-dashed rounded-md p-6 text-center transition-colors",
          dragOver
            ? "border-accent bg-accent/5 text-fg"
            : "border-border/60 text-muted hover:border-accent/60 hover:text-fg"
        )}
      >
        <div className="text-sm">
          {mutation.isPending
            ? "Uploading…"
            : `Drop ${kind} file (${accept}) here, or click to choose.`}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) submit(f);
            e.target.value = "";
          }}
        />
      </div>
      {error && <div className="text-negRed text-xs">{error}</div>}
      {result && <ResultSummary result={result} />}
    </div>
  );
}

function ResultSummary({ result }: { result: UploadResult }) {
  return (
    <div className="border border-border/60 rounded p-2 text-xs space-y-1">
      {result.duplicate && (
        <div className="text-muted">
          Duplicate upload — file already ingested (id: {result.fileId?.slice(0, 8)}…).
        </div>
      )}
      {!result.duplicate && (
        <div className="font-mono">
          {result.rows !== undefined && <>rows {result.rows} · </>}
          {result.parsed !== undefined && <>parsed {result.parsed} · </>}
          {result.inserted !== undefined && <>inserted {result.inserted} · </>}
          {result.updated !== undefined && <>updated {result.updated} · </>}
          {result.duplicates !== undefined && <>duplicates {result.duplicates} · </>}
          {result.skipped !== undefined && <>skipped {result.skipped}</>}
        </div>
      )}
      {result.warnings && result.warnings.length > 0 && (
        <details className="text-muted">
          <summary>{result.warnings.length} warning(s)</summary>
          <ul className="pl-4 list-disc">
            {result.warnings.slice(0, 20).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function HistoryTable() {
  const filesQ = useQuery({
    queryKey: ["ingest-files"],
    queryFn: () => api<{ files: IngestFile[] }>("/ingest/files"),
  });

  return (
    <section className="space-y-2">
      <h2 className="font-semibold">History</h2>
      {filesQ.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {filesQ.isError && (
        <div className="text-negRed text-sm">
          {(filesQ.error as Error).message}
        </div>
      )}
      {filesQ.data && filesQ.data.files.length === 0 && (
        <div className="text-muted text-sm">No files uploaded yet.</div>
      )}
      {filesQ.data && filesQ.data.files.length > 0 && (
        <div className="overflow-auto border border-border/60 rounded-md">
          <table className="min-w-full text-xs">
            <thead className="bg-bg sticky top-0 text-muted">
              <tr>
                <Th>Uploaded</Th>
                <Th>Kind</Th>
                <Th>Filename</Th>
                <Th className="text-right">Rows</Th>
                <Th className="text-right">Size</Th>
                <Th>Warnings</Th>
                <Th>By</Th>
                <Th>SHA-256</Th>
              </tr>
            </thead>
            <tbody>
              {filesQ.data.files.map((f) => (
                <tr key={f.id} className="border-t border-border/30 hover:bg-border/10">
                  <Td>{formatTimestamp(f.uploadedAt)}</Td>
                  <Td>{f.kind}</Td>
                  <Td className="font-mono">{f.filename}</Td>
                  <Td className="text-right">{f.rowCount ?? "—"}</Td>
                  <Td className="text-right">{formatBytes(f.sizeBytes)}</Td>
                  <Td>
                    {f.parseWarnings && f.parseWarnings.length > 0
                      ? `${f.parseWarnings.length}`
                      : "—"}
                  </Td>
                  <Td>{f.uploaderDisplayName ?? f.uploaderEmail ?? "—"}</Td>
                  <Td className="font-mono text-muted">{f.sha256.slice(0, 12)}…</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-2 py-1.5 font-normal whitespace-nowrap text-left", className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-2 py-1 whitespace-nowrap", className)}>{children}</td>;
}
